/**
 * `<HiredLedgerStack>` — the sub-900px Hired Candidates surface.
 *
 * Below ~900px a 13-column grid degrades into a horizontal scroll you have to
 * drive with two thumbs while remembering which column you were in. So the
 * narrow layout is the detail-panel shape instead: a list of hires, and one
 * candidate's full field set at a time.
 *
 * The founder's rules survive the shape change:
 *   • the candidate's NAME still navigates (to their candidate page) and remains
 *     the only navigating element in the panel header;
 *   • selecting a row opens the PANEL — it does not navigate;
 *   • every variable is still edited inline, on blur, via the same `<LedgerCell>`
 *     as the grid, so there is one editing behaviour to learn and one to test.
 */
import { useState, type JSX } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { CandidatePhoto } from "@/components/candidates/CandidatePhoto";
import type { HiredLedgerItem } from "@/hooks/queries/outcomes/useHiredLedger";
import { LedgerCell } from "./LedgerCell";
import { CompletenessMeter } from "./CompletenessMeter";
import {
  fieldsForGroup,
  type LedgerColumnGroup,
  type LedgerFieldKey,
} from "./ledgerFields";

interface HiredLedgerStackProps {
  rows: HiredLedgerItem[];
  group: LedgerColumnGroup;
  onCellCommit: (
    row: HiredLedgerItem,
    field: LedgerFieldKey,
    value: string | boolean | null,
  ) => void;
  cellErrors: Record<string, string>;
  base: string;
  canEdit: boolean;
  emptyState?: JSX.Element;
}

export function HiredLedgerStack({
  rows,
  group,
  onCellCommit,
  cellErrors,
  base,
  canEdit,
  emptyState,
}: HiredLedgerStackProps): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((r) => r.candidate_id === selectedId) ?? null;

  if (rows.length === 0 && emptyState) {
    return <div data-testid="ledger-stack">{emptyState}</div>;
  }

  if (selected) {
    const fields = fieldsForGroup(group);
    return (
      <div
        className="rounded-[var(--radius)] border border-border-default bg-surface-card shadow-sm"
        data-testid="ledger-stack-panel"
      >
        <div className="flex items-center gap-[10px] border-b border-border-default p-[12px]">
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            aria-label="Back to the hired list"
            data-testid="ledger-panel-back"
            className={cn(
              "inline-flex h-8 shrink-0 items-center rounded-[var(--radius)] px-[8px]",
              "text-[12.5px] text-text-secondary transition-colors",
              "hover:bg-surface-subtle hover:text-text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
            )}
          >
            ← Back
          </button>
          <CandidatePhoto
            candidateId={selected.candidate_id}
            candidateName={selected.candidate_name}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <Link
              to={`${base}/candidates/${selected.candidate_id}`}
              className={cn(
                "block truncate text-text-primary hover:underline",
                "rounded-sm focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-border-focus",
              )}
              style={{ fontWeight: 600 }}
            >
              {selected.candidate_name}
            </Link>
            <div className="truncate text-[12px] text-text-secondary">
              {selected.role ?? "Role not recorded"}
            </div>
          </div>
          <CompletenessMeter
            filled={selected.completeness}
            total={selected.completeness_total}
          />
        </div>

        <div className="divide-y divide-border-default">
          {fields.map((field) => (
            <div
              key={field.key}
              className="flex items-center gap-[12px] px-[12px] py-[8px]"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] text-text-primary">{field.label}</div>
                <div className="text-[11.5px] text-text-tertiary">{field.help}</div>
              </div>
              <div style={{ width: "168px" }}>
                <LedgerCell
                  field={field}
                  value={selected[field.key]}
                  candidateName={selected.candidate_name}
                  disabled={!canEdit}
                  errorMessage={cellErrors[`${selected.candidate_id}:${field.key}`] ?? null}
                  testId={`ledger-panel-cell-${field.key}`}
                  onCommit={(value) => onCellCommit(selected, field.key, value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ul
      className="flex flex-col gap-[8px]"
      data-testid="ledger-stack"
      aria-label="Hired candidates"
    >
      {rows.map((row) => (
        <li key={row.candidate_id}>
          <div
            className={cn(
              "flex items-center gap-[10px] rounded-[var(--radius)] border",
              "border-border-default bg-surface-card p-[10px] shadow-sm",
            )}
          >
            <CandidatePhoto
              candidateId={row.candidate_id}
              candidateName={row.candidate_name}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              {/* Still the only navigating element. */}
              <Link
                to={`${base}/candidates/${row.candidate_id}`}
                className={cn(
                  "block truncate text-text-primary hover:underline",
                  "rounded-sm focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-border-focus",
                )}
                style={{ fontWeight: 600 }}
              >
                {row.candidate_name}
              </Link>
              <div className="truncate text-[12px] text-text-secondary">
                {row.role ?? "Role not recorded"}
              </div>
            </div>
            <CompletenessMeter filled={row.completeness} total={row.completeness_total} />
            <button
              type="button"
              onClick={() => setSelectedId(row.candidate_id)}
              data-testid={`ledger-stack-open-${row.candidate_id}`}
              aria-label={`Record outcomes for ${row.candidate_name}`}
              className={cn(
                "inline-flex h-8 shrink-0 items-center rounded-[var(--radius)]",
                "border border-border-default px-[10px] text-[12.5px] text-text-secondary",
                "transition-colors hover:bg-surface-subtle hover:text-text-primary",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
              )}
            >
              Record
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
